import React, { useState } from "react";
import { EventFormData, Person } from "../../types";
import { TrashIcon, WarningIcon, PlusIcon, MinusIcon, InfoIcon } from "@phosphor-icons/react";
import DropdownSelect from "../common/DropdownSelect";
import { SurpriseEventHelpModal } from "../common";
import "./events.css";

interface EventCreationProps {
  groupId: number;
  persons: Person[];
  currentPersonId?: number;
  onEventCreated: (event: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
  initialData?: {
    name: string;
    eventDate: string;
    bookingStartAt?: string;
    bookingEndAt?: string;
    isSurprise?: boolean;
  };
  isEditing?: boolean;
  initialSelectedPersons?: number[];
}

const EventCreation: React.FC<EventCreationProps> = ({
  groupId,
  persons,
  currentPersonId,
  onEventCreated,
  onCancel,
  isLoading = false,
  error,
  initialData,
  isEditing = false,
  initialSelectedPersons = [],
}) => {
  const [formData, setFormData] = useState<EventFormData>({
    name: initialData?.name || "",
    eventDate: initialData?.eventDate || "",
    bookingStartAt: initialData?.bookingStartAt || "",
    bookingEndAt: initialData?.bookingEndAt || "",
    isSurprise: initialData?.isSurprise ?? true,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [activateAllLists, setActivateAllLists] = useState(
    isEditing ? initialSelectedPersons.length === persons.length : false
  );
  const [selectedPersons, setSelectedPersons] = useState<number[]>(
    isEditing ? initialSelectedPersons : []
  );

  // TEMPORARILY HIDDEN - State to control if booking section is expanded
  // const [isBookingSectionExpanded, setIsBookingSectionExpanded] = useState(
  //   // Auto-expand if booking dates are already set
  //   !!(initialData?.bookingStartAt || initialData?.bookingEndAt)
  // );

  // Stato per controllare se la sezione modalità evento è espansa
  const [isEventModeSectionExpanded, setIsEventModeSectionExpanded] = useState(false);

  // Stato per controllare se la sezione persone è espansa
  const [isPersonsSectionExpanded, setIsPersonsSectionExpanded] = useState(!isEditing);

  // Stato per il modal di aiuto
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Calcola il numero di persone selezionate
  const getSelectedPersonsCount = () => {
    if (activateAllLists) {
      return persons.length;
    }
    return selectedPersons.length;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = "Il nome dell'evento è obbligatorio";
    }

    if (!formData.eventDate) {
      errors.eventDate = "La data dell'evento è obbligatoria";
    }

    // TEMPORARILY HIDDEN - Validate booking dates if provided
    // if (formData.bookingStartAt && formData.bookingEndAt) {
    //   const startDate = new Date(formData.bookingStartAt);
    //   const endDate = new Date(formData.bookingEndAt);
    //   if (startDate >= endDate) {
    //     errors.bookingEndAt =
    //       "La data di fine prenotazioni deve essere successiva a quella di inizio";
    //   }
    // }

    // Validate person selection (both creating and editing)
    if (!activateAllLists && selectedPersons.length === 0) {
      errors.persons = "Seleziona almeno una persona per l'evento";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      onEventCreated({
        ...formData,
        activateAllLists,
        selectedPersons: activateAllLists
          ? persons.map((p) => p.id)
          : selectedPersons,
      });
    } catch (error) {
      console.error("Failed to create event:", error);
    }
  };

  const handleInputChange = (
    field: keyof EventFormData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Get today's date for min date validation
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="event-creation-form">
      {error && (
        <div className="modern-error-message">
          <div className="error-content">
            <WarningIcon size={20} weight="fill" />
            <p>{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="event-form">
        <div className="form-group">
          <label htmlFor="eventName">Nome Evento *</label>
          <input
            id="eventName"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="es. Natale 2025, Compleanno Marco..."
            className={formErrors.name ? "error" : ""}
            disabled={isLoading}
          />
          {formErrors.name && (
            <div className="field-error">{formErrors.name}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="eventDate">Data Evento *</label>
          <input
            id="eventDate"
            type="date"
            value={formData.eventDate}
            onChange={(e) => handleInputChange("eventDate", e.target.value)}
            min={today}
            className={formErrors.eventDate ? "error" : ""}
            disabled={isLoading}
          />
          {formErrors.eventDate && (
            <div className="field-error">{formErrors.eventDate}</div>
          )}
        </div>

        {/* TEMPORARILY HIDDEN - Booking Period (Optional) */}
        {/* 
        <div className="form-section">
          <div 
            className="collapsible-header"
            onClick={() => setIsBookingSectionExpanded(!isBookingSectionExpanded)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsBookingSectionExpanded(!isBookingSectionExpanded);
              }
            }}
            role="button"
            tabIndex={0}
            aria-expanded={isBookingSectionExpanded}
            aria-controls="booking-section-content"
          >
            <h4>Periodo Prenotazioni (Opzionale)</h4>
            <button type="button" className="collapse-toggle">
              {isBookingSectionExpanded ? (
                <MinusIcon size={20} weight="bold" />
              ) : (
                <PlusIcon size={20} weight="bold" />
              )}
            </button>
          </div>
          
          {isBookingSectionExpanded && (
            <div id="booking-section-content">
              <p className="field-help">
                Definisci quando i membri possono iniziare e smettere di prenotare
                regali
              </p>

              <div className="form-group">
                <label htmlFor="bookingStart">Inizio Prenotazioni</label>
                <input
                  id="bookingStart"
                  type="datetime-local"
                  value={formData.bookingStartAt}
                  onChange={(e) =>
                    handleInputChange("bookingStartAt", e.target.value)
                  }
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="bookingEnd">Fine Prenotazioni</label>
                <input
                  id="bookingEnd"
                  type="datetime-local"
                  value={formData.bookingEndAt}
                  onChange={(e) =>
                    handleInputChange("bookingEndAt", e.target.value)
                  }
                  className={formErrors.bookingEndAt ? "error" : ""}
                  disabled={isLoading}
                />
                {formErrors.bookingEndAt && (
                  <div className="field-error">{formErrors.bookingEndAt}</div>
                )}
              </div>
            </div>
          )}
        </div>
        */}

        <div className="form-section">
          <div 
            className="collapsible-header"
            onClick={() => setIsEventModeSectionExpanded(!isEventModeSectionExpanded)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsEventModeSectionExpanded(!isEventModeSectionExpanded);
              }
            }}
            role="button"
            tabIndex={0}
            aria-expanded={isEventModeSectionExpanded}
            aria-controls="event-mode-section-content"
          >
            <h4>{formData.isSurprise ? "Modalità evento a sorpresa" : "Modalità evento normale"}</h4>
            <button type="button" className="collapse-toggle">
              {isEventModeSectionExpanded ? (
                <MinusIcon size={20} weight="bold" />
              ) : (
                <PlusIcon size={20} weight="bold" />
              )}
            </button>
          </div>
          
          {isEventModeSectionExpanded && (
            <div id="event-mode-section-content">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isSurprise || false}
                    onChange={(e) => handleInputChange("isSurprise", e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="checkbox-text">
                    Evento a sorpresa
                  </span>
                </label>
                <p className="field-help">
                  <strong>La modalità evento a sorpresa</strong> mantiene segreti i regali fino al giorno dell'evento. 
                  Chi riceverà i regali potrà pubblicare la suo lista solo quando sarà pronto e non vedrà quali regali sono stati prenotati.<br/><br/>
                  <strong>Se l'evento non è a sorpresa</strong>, chi riceverà i regali potrà vedere quali sono stati prenotati, <i>ma non da chi</i>.<br/> 
                  Potrà inoltre modificare o cancellare i regali non ancora prenotati.
                </p>
                <button
                  type="button"
                  className="help-link"
                  onClick={() => setShowHelpModal(true)}
                >
                  <InfoIcon size={16} weight="fill" />
                  Come funziona?
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Lista Activation Section - Show both when creating and editing */}
        {
          <div className="form-section">
            <div 
              className="collapsible-header"
              onClick={() => setIsPersonsSectionExpanded(!isPersonsSectionExpanded)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsPersonsSectionExpanded(!isPersonsSectionExpanded);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={isPersonsSectionExpanded}
              aria-controls="persons-section-content"
            >
              <h4>Chi riceverà i regali? {getSelectedPersonsCount()} {getSelectedPersonsCount() === 1 ? 'Persona' : 'Persone'}</h4>
              <button type="button" className="collapse-toggle">
                {isPersonsSectionExpanded ? (
                  <MinusIcon size={20} weight="bold" />
                ) : (
                  <PlusIcon size={20} weight="bold" />
                )}
              </button>
            </div>
            
            {isPersonsSectionExpanded && (
              <div id="persons-section-content">
                <p className="field-help">
                  Scegli quali persone del gruppo possono ricevere regali per questo
                  evento.<br/>
                  Solo chi riceverà i regali potrà creare la propria lista.
                </p>

            {persons.length <= 1 ? (
              <div className="warning-message">
                <div className="warning-icon">
                  <WarningIcon size={24} weight="fill" />
                </div>
                <div className="warning-content">
                  <h5>Ci sei solo tu.</h5>
                  <p>
                    Prima di creare un evento, aggiungi altri membri al gruppo
                    nella sezione "Membri e Amministratori".
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={activateAllLists}
                      onChange={(e) => {
                        setActivateAllLists(e.target.checked);
                        if (e.target.checked) {
                          // Se attiva tutti, seleziona tutte le persone
                          setSelectedPersons(persons.map((p) => p.id));
                        } else {
                          // Se disattiva, deseleziona tutti
                          setSelectedPersons([]);
                        }
                      }}
                      disabled={isLoading}
                    />
                    <span className="checkbox-text">
                      Abilita tutte le persone del gruppo ({persons.length}{" "}
                      persone)
                    </span>
                  </label>
                </div>

                {/* Selezione individuale persone quando "attiva tutti" è disattivato */}
                {!activateAllLists && (
                  <div className="persons-selection">
                    <h5>Seleziona le persone:</h5>
                    <div className="persons-dropdown">
                      <DropdownSelect
                        value=""
                        placeholder="+ Aggiungi Persona"
                        options={persons
                          .filter(
                            (person) => !selectedPersons.includes(person.id)
                          )
                          .map((person) => ({
                            value: person.id.toString(),
                            label: `${person.name}${
                              person.parentPersonId ? " (Bambino)" : ""
                            }`,
                          }))}
                        onChange={(value) => {
                          const personId = Number.parseInt(value, 10);
                          if (personId && !selectedPersons.includes(personId)) {
                            setSelectedPersons((prev) => [...prev, personId]);
                          }
                        }}
                      />
                    </div>

                    {/* Lista delle persone selezionate */}
                    {selectedPersons.length > 0 && (
                      <div className="selected-persons">
                        <h6>Persone selezionate:</h6>
                        <div className="selected-persons-list">
                          {selectedPersons.map((personId) => {
                            const person = persons.find(
                              (p) => p.id === personId
                            );
                            if (!person) return null;
                            return (
                              <div
                                key={personId}
                                className="selected-person-item"
                              >
                                <span className="person-name">
                                  {person.name}
                                  {person.parentPersonId && (
                                    <span
                                      className="person-type"
                                      style={{
                                        marginLeft: "5px",
                                        padding: "0.2rem 0.3rem",
                                      }}
                                    >
                                      {" "}
                                      Bambino
                                    </span>
                                  )}
                                </span>
                                <button
                                  type="button"
                                  className="remove-person-btn"
                                  onClick={() => {
                                    setSelectedPersons((prev) =>
                                      prev.filter((id) => id !== personId)
                                    );
                                  }}
                                  disabled={isLoading}
                                  title={`Rimuovi ${person.name}`}
                                >
                                  <TrashIcon size={18} weight="bold" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {selectedPersons.length === 0 && (
                      <div className="field-help warning">
                        Seleziona almeno una persona per creare l'evento.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
              </div>
            )}
          </div>
        }

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="cancel-button"
            disabled={isLoading}
          >
            Annulla
          </button>
          <button
            type="submit"
            className="create-event-button"
            disabled={
              isLoading ||
              persons.length <= 1 ||
              (!activateAllLists && selectedPersons.length === 0)
            }
          >
            {isLoading
              ? isEditing
                ? "Salvando..."
                : "Creazione..."
              : isEditing
              ? "Salva Modifiche"
              : persons.length <= 1
              ? "Aggiungi altri membri prima"
              : "Crea Evento"}
          </button>
        </div>
      </form>

      {/* Modal di aiuto per eventi sorpresa */}
      <SurpriseEventHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  );
};

export default EventCreation;
